/**
 * Turns a scraped page's raw text into text a claim can be read off.
 *
 * WHY THIS EXISTS. A scraper hands back everything on the page: the site's
 * navigation, a corrections notice, the byline, and (on news sites) a
 * "republish this story" widget whose payload is the article again as ESCAPED
 * HTML. A fixed character budget spent on that text leaves the reader of the
 * excerpt with `&lt;h1&gt;` and "Close Search for: Search Investigations
 * Guides" where the reporting should be, and the first "claims" pulled off it
 * were the headline, the byline and half a tag. Every downstream step (angle
 * ranking, concept hooks, the ad copy's evidence list) quotes those claims
 * verbatim, so the damage is not cosmetic: a byline fragment became a concept's
 * proof line and an ad's citable evidence.
 *
 * Pure and dependency-free. `supabase/functions/_shared/clean-source-text.ts`
 * re-exports this file so the edge fetch and the Node pipeline apply one
 * definition, never two that drift.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
  reg: '®',
  copy: '©',
  trade: '™',
  bull: '•',
  middot: '·',
  laquo: '«',
  raquo: '»',
  times: '×',
  deg: '°',
  euro: '€',
  pound: '£',
  cent: '¢',
  yen: '¥',
  sect: '§',
  para: '¶',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  rarr: '→',
  larr: '←',
  eacute: 'é',
  egrave: 'è',
  aacute: 'á',
  iacute: 'í',
  oacute: 'ó',
  uacute: 'ú',
  ntilde: 'ñ',
  ccedil: 'ç',
  uuml: 'ü',
  ouml: 'ö',
  auml: 'ä',
  ensp: ' ',
  emsp: ' ',
  thinsp: ' ',
  shy: '',
  zwj: '',
  zwnj: '',
};

const CASE_INSENSITIVE_ENTITIES = new Set(['amp', 'lt', 'gt', 'quot', 'nbsp', 'reg', 'copy']);

/** Decodes the HTML entities a scraper leaves behind, including double-escaped ones. */
export function decodeHtmlEntities(text: string): string {
  const once = (input: string): string =>
    input.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
      if (body[0] === '#') {
        const code =
          body[1] === 'x' || body[1] === 'X'
            ? Number.parseInt(body.slice(2), 16)
            : Number.parseInt(body.slice(1), 10);
        if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return whole;
        const char = String.fromCodePoint(code);
        return code === 0xa0 ? ' ' : char;
      }
      const lower = body.toLowerCase();
      return (
        NAMED_ENTITIES[body] ??
        (CASE_INSENSITIVE_ENTITIES.has(lower) ? NAMED_ENTITIES[lower] : undefined) ??
        whole
      );
    });
  // Twice: "&amp;lt;" is a real shape when a page escapes its own escaped embed.
  return once(once(text));
}

const MONTH =
  '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?';

/** "by Paul Cobler, The Texas Tribune September 21, 2026" and the "Sept. 21, 2026, 4:31 p.m. Central" variant. */
const BYLINE_AUTHOR = "[A-Z][\\w.'’-]+(?:\\s+[A-Z][\\w.'’-]+){0,3}";

const BYLINE_WITH_DATE = new RegExp(
  `\\b[Bb]y\\s+${BYLINE_AUTHOR}(?:,\\s*(?:[A-Z][\\w.'’&-]*\\s+){0,5}|,?\\s+)${MONTH}\\s+\\d{1,2},\\s+\\d{4}(?:,\\s+\\d{1,2}:\\d{2}\\s*[ap]\\.?m\\.?(?:\\s+[A-Z][a-z]+)?)?`,
  'g',
);

const WIDGET_MARKERS = [
  /\bHTML\s+Plain\s+Text\b/g,
  /\bRepublish\s+(?:this|our)\s+(?:story|article|articles|work)\b[^.]*\./gi,
  /\bClose\s+window\b/gi,
  /\[email(?:\s|&#160;| )*protected\]/gi,
];

/**
 * Strips markup and the widget text a news page wraps around its article. The
 * result is one line of prose with sentence breaks left in place.
 */
export function cleanSourceText(raw: string): string {
  if (!raw) return '';
  let text = decodeHtmlEntities(raw);
  text = text.replace(/<\s*br\s*\/?>/gi, ' ').replace(/<\/(?:p|div|h[1-6]|li)>/gi, '. ');
  text = text.replace(/<[^>]{0,400}>/g, ' ');
  for (const marker of WIDGET_MARKERS) text = text.replace(marker, ' ');
  // The byline is kept, as a sentence of its own: it is where the publication
  // and the date come from (`findPublication`), and `looksLikePageFurniture`
  // keeps it out of the claims.
  text = text.replace(
    BYLINE_WITH_DATE,
    (byline) => `. ${byline.charAt(0).toUpperCase()}${byline.slice(1)}. `,
  );
  return text
    .replace(/\.\s*\.(?:\s*\.)*/g, '.')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
}

const PUBLICATION = new RegExp(
  `\\b[Bb]y\\s+${BYLINE_AUTHOR}(?:,\\s*(?<outlet>(?:[A-Z][\\w.'’&-]*\\s+){1,5}?)|,?\\s+)(?<date>${MONTH}\\s+\\d{1,2},\\s+\\d{4})`,
);

export interface Publication {
  /** The outlet exactly as the byline prints it ("The Texas Tribune"), or null when the byline names none. */
  outlet: string | null;
  /** The publication date exactly as printed ("Sept. 21, 2026"). */
  published: string;
}

/**
 * Who published the page and when, read off its own byline. Null when the page
 * prints no byline with a date; nothing is inferred from the fetch time,
 * because the day a page was fetched is not the day it was published.
 */
export function findPublication(raw: string): Publication | null {
  const found: Publication[] = [];
  for (const match of cleanSourceText(raw).matchAll(new RegExp(PUBLICATION.source, 'g'))) {
    const date = match.groups?.date?.trim();
    if (!date) continue;
    const outlet = match.groups?.outlet?.trim().replace(/[,.]+$/, '') || null;
    found.push({ outlet, published: date });
  }
  return found.find((entry) => entry.outlet !== null) ?? found[0] ?? null;
}

/**
 * The headline (and deck) a page prints immediately before its byline. It is
 * the article's title, not something the article asserts, so it must not be
 * offered downstream as a claim. Found positionally: whatever sentence sits
 * directly before "by <Name> … <date>".
 */
export function findPageHeadlines(raw: string): string[] {
  if (!raw) return [];
  let text = decodeHtmlEntities(raw);
  text = text.replace(/<\s*br\s*\/?>/gi, ' ').replace(/<\/(?:p|div|h[1-6]|li)>/gi, '. ');
  text = text.replace(/<[^>]{0,400}>/g, ' ');
  for (const marker of WIDGET_MARKERS) text = text.replace(marker, ' ');
  text = text.replace(/\s+/g, ' ');
  const headlines: string[] = [];
  for (const match of text.matchAll(BYLINE_WITH_DATE)) {
    const at = match.index ?? 0;
    const before = text.slice(Math.max(0, at - 260), at).trim();
    const chunks = before.split(/(?<=[.!?])\s+/).filter((chunk) => chunk.trim().length > 0);
    const last = chunks[chunks.length - 1]?.trim();
    if (last && last.length >= 20) headlines.push(last);
  }
  return headlines;
}

/** Words that mark a sentence as page furniture rather than reporting. */
const CHROME_TERMS = [
  'close',
  'search',
  'menu',
  'donate',
  'newsletter',
  'newsletters',
  'subscribe',
  'republish',
  'skip to content',
  'sign in',
  'log in',
  'login',
  'privacy',
  'cookie',
  'cookies',
  'advertisement',
  'read more',
  'share this',
  'posted in',
  'corrections',
  'contact',
  'jobs',
  'staff',
  'events',
  'topic menu',
  'open search',
  'trusted by',
  'all rights reserved',
  'terms of use',
  'follow us',
  'facebook',
  'twitter',
  'instagram',
];

const CHROME_TERM_RES = CHROME_TERMS.map(
  (term) => new RegExp(`(?:^|[^a-z])${term.replace(/\s+/g, '\\s+')}(?:$|[^a-z])`, 'i'),
);

/** True when a sentence is site navigation, a byline, a dateline or a widget label. */
export function looksLikePageFurniture(sentence: string): boolean {
  const text = sentence.trim();
  if (!text) return true;
  if ((text.match(/\s\|\s/g) ?? []).length >= 1) return true;
  if (/^(?:by|posted in|filed under|tags?|share|photo|credit)\b[:\s]/i.test(text)) return true;
  if (/^(?:close|skip to|open search|menu)\b/i.test(text)) return true;
  const hits = CHROME_TERM_RES.filter((re) => re.test(text)).length;
  if (hits >= 3) return true;
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length >= 8) {
    const capitalised = words.filter((word) => /^[A-Z0-9]/.test(word)).length;
    if (capitalised / words.length > 0.75) return true;
  }
  return false;
}

const ABBREVIATION_DOT =
  /\b(Gov|Sen|Rep|Dr|Mr|Mrs|Ms|Jr|Sr|St|Inc|Corp|Co|Ltd|vs|No|Dept|Lt|Col|Gen|Sgt|Prof|Sept|Oct|Nov|Dec|Jan|Feb|Aug|Mar|Apr)\./g;
const DOT_MASK = '․';

/** Sentence split that does not cut "Gov. Greg Abbott" after "Gov.". */
export function splitSentences(text: string): string[] {
  const masked = text.replace(ABBREVIATION_DOT, (_whole, abbreviation: string) => {
    return `${abbreviation}${DOT_MASK}`;
  });
  return masked
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/)
    .map((part) => part.split(DOT_MASK).join('.').trim())
    .filter(Boolean);
}

/**
 * The text an excerpt budget should be spent on: markup and widgets removed,
 * page furniture dropped, exact repeats collapsed, the page's own headline
 * kept once at the front so a reader of the excerpt still sees what the page
 * is. Falls back to the cleaned text when filtering would leave nothing, so a
 * page the furniture heuristics misjudge is never emptied.
 */
export function articleExcerpt(raw: string): string {
  const cleaned = cleanSourceText(raw);
  if (!cleaned) return '';
  const seen = new Set<string>();
  const kept: string[] = [];
  // ONE byline survives, because the publication and the date are read off it
  // (`findPublication`); the best one wins, and a byline that names the
  // outlet beats one that gives only a date.
  let bylineAt = -1;
  let bylineNamesOutlet = false;
  for (const sentence of splitSentences(cleaned)) {
    const byline = PUBLICATION.exec(sentence);
    if (byline) {
      const namesOutlet = Boolean(byline.groups?.outlet?.trim());
      if (bylineAt === -1) {
        bylineAt = kept.length;
        bylineNamesOutlet = namesOutlet;
        kept.push(sentence);
      } else if (namesOutlet && !bylineNamesOutlet) {
        kept[bylineAt] = sentence;
        bylineNamesOutlet = true;
      }
      continue;
    }
    if (looksLikePageFurniture(sentence)) continue;
    const key = sentence
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    if (key.length > 0 && seen.has(key)) continue;
    seen.add(key);
    kept.push(sentence);
  }
  return kept.length > 0 ? kept.join(' ') : cleaned;
}
